import {
  AiAssistantApprovalCard,
  AiAssistantApprovalResponse,
  AiAssistantToolPart,
} from "@/components/ai-assistant/AiAssistantApprovalCard.tsx";
import { handleDatesResponse } from "@/helpers/handle-date.ts";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import useGoalsHook from "@/hooks/use-goals.hook.tsx";
import { useGoal } from "@/queries/goals.query.ts";
import { ApiCustomFieldValue } from "@/types/custom-field.types.ts";
import {
  ApiGoalMetricType,
  GoalConfidenceLevelItems,
  GoalMetricTypeItems,
  GoalOwnerTypeItems,
  GoalStatusItems,
} from "@/types/goals.types.ts";
import { EmojiDisplay, SelectItem } from "@skailer-com/uikit";
import { getToolName } from "ai";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";

const FIELD_LABELS: Record<string, string> = {
  title: "common:title",
  description: "common:description",
  parent_goal_id: "goals:parent_goal",
  owner_type: "goals:owner_type",
  owner_id: "common:owner",
  assign_to_id: "common:assign_to",
  is_shared: "common:visibility",
  visible_to_ids: "goals:visible_to",
  date_start: "common:start_date",
  date_end: "common:end_date",
  status: "common:status",
  confidence_level: "goals:confidence_level",
  weight: "goals:weight",
  metric_type: "goals:metric_type",
  start_value: "goals:start_value",
  target_value: "goals:target_value",
  current_value: "goals:current_value",
};

const HIDDEN_FIELDS = new Set(["confirmation_token"]);

const isHiddenField = (key: string) =>
  HIDDEN_FIELDS.has(key) || (!FIELD_LABELS[key] && /_ids?$/.test(key));

const isPrimitive = (value: unknown): value is string | number | boolean =>
  ["string", "number", "boolean"].includes(typeof value);

const countFormQuestions = (form: { pages?: { sections?: { elements?: unknown[] }[] }[] }) =>
  (form.pages ?? [])
    .flatMap((page) => page.sections ?? [])
    .flatMap((section) => section.elements ?? []).length;

const humanizeKey = (key: string) => {
  const text = key.replace(/_/g, " ");
  return text.charAt(0).toUpperCase() + text.slice(1);
};

const SELECT_FIELDS: Record<string, () => SelectItem[]> = {
  status: GoalStatusItems,
  confidence_level: GoalConfidenceLevelItems,
  owner_type: GoalOwnerTypeItems,
  metric_type: GoalMetricTypeItems,
};

interface AiAssistantFieldsCardProps {
  part: AiAssistantToolPart;
  onApprovalResponse: AiAssistantApprovalResponse;
}

export function AiAssistantFieldsCard({ part, onApprovalResponse }: AiAssistantFieldsCardProps) {
  const { t } = useTranslation();
  const { renderDate } = useFormatter();
  const { renderKeyResultValue } = useGoalsHook();

  const input = (part.input ?? {}) as Record<string, unknown>;
  const goalId = typeof input.goal_id === "number" ? input.goal_id : undefined;
  const { data: goal } = useGoal(goalId ?? 0, goalId !== undefined);
  const keyResult = goal?.goal_key_results.find((item) => item.id === input.key_result_id);
  const metricType = (input.metric_type ?? keyResult?.metric_type) as ApiGoalMetricType;

  const parsedInput = structuredClone(input);
  handleDatesResponse(parsedInput);

  const renderPrimitive = (value: string | number | boolean) =>
    typeof value === "boolean" ? t(value ? "common:yes" : "common:no") : String(value);

  const renderValue = (key: string, value: unknown): ReactNode => {
    const parsed = parsedInput[key];
    if (parsed instanceof Date) {
      return isNaN(parsed.getTime()) ? String(value) : renderDate(parsed);
    }
    if (SELECT_FIELDS[key]) {
      return SELECT_FIELDS[key]().find((item) => item.id === value)?.title ?? String(value);
    }
    if (key === "is_shared") {
      return t(value ? "goals:privacy_public" : "goals:privacy_private");
    }
    if (key.endsWith("_value") && typeof value === "object") {
      return renderKeyResultValue(value as ApiCustomFieldValue, metricType);
    }
    if (key === "emoji" && typeof value === "string") {
      return <EmojiDisplay id={value} size={"xs"} />;
    }
    if (key === "form" && typeof value === "object") {
      return t("ai:assistant_field_questions", { count: countFormQuestions(value as object) });
    }
    if ((key === "type" || key === "target_type") && typeof value === "string") {
      return t([`ai:assistant_value_${value}`, value]);
    }
    if (Array.isArray(value)) {
      if (value.every(isPrimitive)) return value.map(renderPrimitive).join(", ");
      return t("ai:assistant_field_items", { count: value.length });
    }
    // Вложенный объект: в карточку попадают только простые значения, сам объект пользователю не показываем
    if (typeof value === "object" && value !== null) {
      const parts = Object.values(value).flatMap((item) =>
        isPrimitive(item)
          ? [renderPrimitive(item)]
          : Array.isArray(item) && item.every(isPrimitive)
            ? [item.map(renderPrimitive).join(", ")]
            : []
      );
      return parts.length > 0 ? parts.join(" · ") : null;
    }
    return isPrimitive(value) ? renderPrimitive(value) : null;
  };

  const rows = Object.entries(input).flatMap(([key, value]) => {
    if (value == null || isHiddenField(key)) return [];
    const rendered = renderValue(key, value);
    if (rendered === null || rendered === "") return [];
    const label = FIELD_LABELS[key]
      ? t(FIELD_LABELS[key])
      : t([`ai:assistant_field_${key}`, humanizeKey(key)]);
    return [{ label, value: rendered }];
  });

  return (
    <AiAssistantApprovalCard
      part={part}
      title={t([`ai:assistant_action_${getToolName(part)}`, "ai:assistant_action_default"])}
      subtitle={[goal?.title, keyResult?.title].filter(Boolean).join(" · ") || undefined}
      rows={rows}
      onApprovalResponse={onApprovalResponse}
    />
  );
}
